import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import './BuildingChart.css'; // Используем те же стили, что и для BuildingChart
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import API_URL from './config';

// Регистрируем необходимые компоненты Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Более яркие и контрастные цвета
const colors = [
  '#FF5722', // Оранжево-красный
  '#2196F3', // Ярко-синий
  '#4CAF50', // Зеленый
  '#9C27B0', // Фиолетовый
  '#FFC107', // Янтарный
  '#E91E63', // Розовый
  '#00BCD4', // Голубой
  '#8BC34A', // Светло-зеленый
  '#673AB7', // Темно-фиолетовый
  '#FFEB3B'  // Желтый
];

// Оборачиваем в forwardRef для доступа к методам компонента извне
const HorizontChart = forwardRef(({ 
  showControls = true,
  externalHouseId = "",
  externalDateFrom = "",
  externalDateTo = ""
}, ref) => {
  const [houses, setHouses] = useState([]);
  const [selectedHouseId, setSelectedHouseId] = useState("");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleChannels, setVisibleChannels] = useState({});
  
  // Добавляем состояния для хранения выбранных дат
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const [dateFrom, setDateFrom] = useState(oneDayAgo.toISOString().slice(0, 16));
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 16));
  
  // Синхронизируем внутренние состояния с внешними значениями
  useEffect(() => {
    if (externalHouseId) setSelectedHouseId(externalHouseId);
  }, [externalHouseId]);

  useEffect(() => {
    if (externalDateFrom) setDateFrom(externalDateFrom);
  }, [externalDateFrom]);
  
  useEffect(() => {
    if (externalDateTo) setDateTo(externalDateTo);
  }, [externalDateTo]);
  
  // Загрузка списка домов с horizont_id
  useEffect(() => {
    const fetchHouses = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Необходима авторизация");
          return;
        }
        
        const response = await fetch(`${API_URL}/Horizont/horizont_houses_list`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`
          },
          credentials: "include"
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка загрузки списка домов: ${response.statusText}`);
        }
        
        const data = await response.json();
        setHouses(data);
      } catch (err) {
        console.error("Ошибка при загрузке списка домов:", err);
        setError("Не удалось загрузить список домов");
      }
    };
    
    fetchHouses();
  }, []);
  
  // Обработчик изменения выбранного дома
  const handleHouseChange = (event) => {
    const newHouseId = event.target.value;
    setSelectedHouseId(newHouseId);
  };
  
  // Обработчики изменения дат
  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
  };
  
  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
  };
  
  // Создаем функцию для обновления данных по кнопке
  const updateChart = useCallback(() => {
    if (!selectedHouseId) {
      setError("Выберите дом для просмотра данных");
      return;
    }
    
    setLoading(true);
    setError(null);
    setChartData(null);
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Необходима авторизация");
          setLoading(false);
          return;
        }
        
        // Преобразуем даты в формат, необходимый для API
        const formattedDateFrom = dateFrom.replace('T', ' ') + ':00';
        const formattedDateTo = dateTo.replace('T', ' ') + ':00';
        
        const response = await fetch(`${API_URL}/Horizont/horizont_data/${selectedHouseId}`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            date_from: formattedDateFrom,
            date_to: formattedDateTo
          }),
          credentials: "include"
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка загрузки данных: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Преобразование данных для графика
        if (data && data.sensors && data.sensors.length > 0) {
          const labels = [];
          const datasets = [];
          const channelVisibility = {};
          
          // Создаем временные метки из первого датчика
          if (data.sensors[0] && data.sensors[0].measurements) {
            data.sensors[0].measurements.forEach(measurement => {
              labels.push(measurement.datetime);
            });
          }
          
          // Обрабатываем датчики и их каналы
          data.sensors.forEach((sensor, sensorIndex) => {
            if (sensor.measurements && sensor.measurements.length > 0) {
              const firstMeasurement = sensor.measurements[0];
              if (firstMeasurement.values) {
                Object.keys(firstMeasurement.values).forEach((channel, channelIndex) => {
                  const channelKey = `sensor_${sensorIndex}_channel_${channel}`;
                  channelVisibility[channelKey] = channelIndex < 5 || 
                    (visibleChannels[channelKey] !== undefined ? visibleChannels[channelKey] : false);
                  
                  const dataPoints = sensor.measurements.map(m => m.values[channel]);
                  
                  datasets.push({
                    label: `${channel}`,
                    data: dataPoints,
                    borderColor: colors[(sensorIndex * 10 + channelIndex) % colors.length],
                    backgroundColor: colors[(sensorIndex * 10 + channelIndex) % colors.length] + '20',
                    tension: 0.3,
                    borderWidth: 2,
                    pointRadius: 2,
                    hidden: !channelVisibility[channelKey]
                  });
                });
              }
            }
          });
          
          setChartData({
            labels,
            datasets
          });
          
          setVisibleChannels(channelVisibility);
        } else {
          setError("Нет данных для отображения");
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError(`Не удалось загрузить данные: ${err.message}`);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedHouseId, dateFrom, dateTo, visibleChannels]);

  // Экспортируем методы для внешнего использования через ref
  useImperativeHandle(ref, () => ({
    updateChart,
    setSelectedHouseId,
    setDateFrom,
    setDateTo
  }));
  
  // Функция переключения видимости канала
  const toggleChannelVisibility = useCallback((channelKey) => {
    setVisibleChannels(prev => ({
      ...prev,
      [channelKey]: !prev[channelKey]
    }));
  }, []);
  
  // Настройки графика
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          color: '#333',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        ticks: {
          color: '#333',
          font: {
            size: 12,
            weight: 'bold'
          },
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    },
    plugins: {
      legend: {
        display: false,
        position: 'top'
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#333',
        bodyColor: '#333',
        padding: 10,
        cornerRadius: 6,
        displayColors: true
      }
    }
  };
  
  // Рендеринг элементов управления
  const renderControls = () => {
    if (!showControls) return null;
    
    return (
        <div className="chart-header">
          <h3>Данные в реальном времени</h3>
          
          <div className="filter-controls">
            <div className="building-selector">
              <select
                value={selectedHouseId}
                onChange={handleHouseChange}
                className="building-select"
                disabled={loading}
              >
                <option value="">Выберите дом</option>
                {houses.map(house => (
                  <option key={house.id} value={house.horizont_id}>
                    {house.address}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="date-selector">
              <div className="date-input-group">
                <label>С:</label>
                <input 
                  type="datetime-local" 
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  disabled={loading}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>По:</label>
                <input 
                  type="datetime-local" 
                  value={dateTo}
                  onChange={handleDateToChange}
                  disabled={loading}
                  className="date-input"
                />
              </div>
              <button 
                className="chart-legend-button active" 
                onClick={updateChart}
                disabled={loading}
              >
                Загрузить
              </button>
            </div>
          </div>
        </div>
    );
  };
        
  // Если данные загружаются
  if (loading) {
    return (
      <div className="building-chart">
        {renderControls()}
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }
  
  // Если произошла ошибка
  if (error) {
    return (
      <div className="building-chart">
        {renderControls()}
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  // Если ничего не выбрано
  if (!selectedHouseId) {
    return (
      <div className="building-chart">
        {renderControls()}
        <div className="error-container">
          <p>Выберите дом из списка и нажмите "Загрузить" для загрузки данных</p>
        </div>
      </div>
    );
  }
  
  // Отрисовка легенды и графика
  const renderLegend = () => {
    if (!chartData || !chartData.datasets) return null;
    
    return (
      <div className="chart-legend-controls">
        {chartData.datasets.map((dataset, index) => {
          const channelKey = `sensor_${Math.floor(index / 10)}_channel_${index % 10}`;
          return (
            <button
              key={`legend-${index}`}
              className={`chart-legend-button ${visibleChannels[channelKey] ? 'active' : 'inactive'}`}
              onClick={() => toggleChannelVisibility(channelKey)}
            >
              <span 
                className="chart-legend-color" 
                style={{ backgroundColor: dataset.borderColor }}
              />
              {dataset.label}
            </button>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="building-chart">
      {renderControls()}
      
      {chartData ? (
        <>
          {renderLegend()}
          <div className="chart-wrapper">
            <Line 
              data={{
                ...chartData,
                datasets: chartData.datasets.map((dataset, index) => {
                  const channelKey = `sensor_${Math.floor(index / 10)}_channel_${index % 10}`;
                  return {
                    ...dataset,
                    hidden: !visibleChannels[channelKey]
                  };
                })
              }}
              options={chartOptions}
            />
          </div>
        </>
      ) : (
        <div className="error-container">
          <p>Нажмите "Загрузить" для загрузки графика</p>
        </div>
      )}
    </div>
  );
});

export default HorizontChart; 